import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import {
  PageCanvas,
  PageHeader,
  RecordSummary,
} from "@/components/page-canvas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  newLocationCustomerDeletionPath,
  editLocationCustomerPath,
  locationCustomersPath,
  locationCustomerPath,
  newLocationVehiclePath,
  locationVehiclePath,
  locationVehicleReusesPath,
  newLocationJobPath,
  newLocationRequestPath,
  locationJobPath,
  locationRequestPath,
} from "@/routes";
import { dateTime, type Job } from "@/pages/jobs/types";
import type { VehicleDetails } from "../vehicles/types";
import type { CustomerProfile } from "./types";

type Vehicle = Pick<
  VehicleDetails,
  "make" | "model" | "registration_number"
> & { id: string };
type Work = {
  jobs: Job[];
  requests: { id: string; state: string; body: string; channel: string }[];
  jobs_page: number;
  jobs_pages: number;
  requests_page: number;
  requests_pages: number;
};
export default function CustomerShow({
  customer,
  vehicles = [],
  available_vehicles = [],
  work,
}: {
  customer: CustomerProfile;
  vehicles: Vehicle[];
  available_vehicles: Vehicle[];
  work: Work;
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const pagePath = (key: "jobs_page" | "requests_page", page: number) =>
    locationCustomerPath(locationId, customer.id, {
      jobs_page: work.jobs_page,
      requests_page: work.requests_page,
      [key]: page,
    });
  const pages = (key: "jobs" | "requests") => (
    <nav
      className="flex flex-wrap gap-3"
      aria-label={t(key === "jobs" ? "jobs.title" : "requests.title")}
    >
      {work[`${key}_page`] > 1 && (
        <Link
          className="underline"
          href={pagePath(`${key}_page`, work[`${key}_page`] - 1)}
        >
          {t("customers.previous")}
        </Link>
      )}
      {work[`${key}_page`] < work[`${key}_pages`] && (
        <Link
          className="underline"
          href={pagePath(`${key}_page`, work[`${key}_page`] + 1)}
        >
          {t("customers.next")}
        </Link>
      )}
    </nav>
  );
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("customers.title"),
          href: locationCustomersPath(locationId),
        },
      ]}
    >
      <Head title={customer.name} />
      <PageCanvas>
        <PageHeader
          title={customer.name}
          description={t("customer_workspace.scope", {
            location: auth.location.name,
          })}
          actions={
            <>
              <Button
                render={
                  <Link
                    href={newLocationJobPath(locationId, {
                      customer_id: customer.id,
                      composition: {
                        origin: "customer",
                        origin_id: customer.id,
                      },
                    })}
                  />
                }
              >
                {t("jobs.create")}
              </Button>
              <Button
                variant="outline"
                render={
                  <Link
                    href={newLocationRequestPath(locationId, {
                      customer_id: customer.id,
                    })}
                  />
                }
              >
                {t("requests.add")}
              </Button>
              <Button
                variant="outline"
                render={
                  <Link
                    href={editLocationCustomerPath(locationId, customer.id)}
                  />
                }
              >
                {t("customers.edit")}
              </Button>
            </>
          }
        />
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)]">
          <div className="min-w-0 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("vehicles.title")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={newLocationVehiclePath(locationId, {
                        customer_id: customer.id,
                      })}
                    />
                  }
                >
                  {t("vehicles.add")}
                </Button>
                {!vehicles.length && (
                  <p className="text-sm text-muted-foreground">
                    {t("vehicles.empty")}
                  </p>
                )}
                <ul className="grid gap-3 md:grid-cols-2">
                  {vehicles.map((vehicle) => (
                    <li key={vehicle.id} className="rounded-lg border p-4">
                      <Link
                        className="font-medium underline"
                        href={locationVehiclePath(locationId, vehicle.id)}
                      >
                        {vehicle.make} {vehicle.model}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        {vehicle.registration_number}
                      </p>
                    </li>
                  ))}
                </ul>
                {available_vehicles.length > 0 && (
                  <section className="space-y-3 border-t pt-4">
                    <p className="text-sm text-muted-foreground">
                      {t("vehicles.reuse_help")}
                    </p>
                    {available_vehicles.map((vehicle) => (
                      <Form
                        key={vehicle.id}
                        action={locationVehicleReusesPath(locationId)}
                        method="post"
                      >
                        {({ processing }) => (
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <input
                              type="hidden"
                              name="customer_id"
                              value={customer.id}
                            />
                            <input
                              type="hidden"
                              name="identity_id"
                              value={vehicle.id}
                            />
                            <span>
                              {vehicle.make} {vehicle.model}{" "}
                              {vehicle.registration_number}
                            </span>
                            <Button
                              type="submit"
                              variant="outline"
                              disabled={processing}
                            >
                              {t("vehicles.reuse")}
                            </Button>
                          </div>
                        )}
                      </Form>
                    ))}
                  </section>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("jobs.title")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!work.jobs.length && (
                  <p className="text-sm text-muted-foreground">
                    {t("customer_workspace.no_jobs")}
                  </p>
                )}
                {work.jobs.map((job) => (
                  <article
                    key={job.id}
                    className="flex flex-wrap items-start justify-between gap-3 border-b pb-4"
                  >
                    <div className="min-w-0">
                      <Link
                        className="font-medium underline"
                        href={locationJobPath(locationId, job.id)}
                      >
                        {job.representation.vehicle.make}{" "}
                        {job.representation.vehicle.model}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        {dateTime(job.starts_at, job.time_zone, i18n.locale)} ·{" "}
                        {job.bay_name}
                      </p>
                    </div>
                    <span className="rounded-md border px-2 py-1 text-sm">
                      {t(`job_board.stages.${job.work_state}`)}
                    </span>
                  </article>
                ))}
                {pages("jobs")}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>
                  <h2>{t("requests.title")}</h2>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!work.requests.length && (
                  <p className="text-sm text-muted-foreground">
                    {t("customer_workspace.no_requests")}
                  </p>
                )}
                {work.requests.map((request) => (
                  <article key={request.id} className="space-y-2 border-b pb-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <Link
                        className="font-medium underline"
                        href={locationRequestPath(locationId, request.id)}
                      >
                        {t(`requests.channels.${request.channel}`)}
                      </Link>
                      <span className="rounded-md border px-2 py-1 text-sm">
                        {t(`requests.states.${request.state}`)}
                      </span>
                    </div>
                    <p className="line-clamp-2 break-words">{request.body}</p>
                  </article>
                ))}
                {pages("requests")}
              </CardContent>
            </Card>
          </div>
          <aside className="min-w-0 space-y-4">
            <RecordSummary title={t("customers.details")}>
              <dl className="space-y-4">
                {(["email", "phone", "address"] as const).map((field) => (
                  <div key={field}>
                    <dt className="text-sm text-muted-foreground">
                      {t(`customers.${field}`)}
                    </dt>
                    <dd className="whitespace-pre-wrap break-words">
                      {customer[field] || t("customers.not_supplied")}
                    </dd>
                  </div>
                ))}
              </dl>
            </RecordSummary>
            {customer.email && (
              <Form
                action={`/${locationId}/customers/${customer.id}/authorization`}
                method="post"
              >
                {({ processing }) => (
                  <Button type="submit" variant="outline" disabled={processing}>
                    {t("customer_portal.invite")}
                  </Button>
                )}
              </Form>
            )}
            {auth.user?.role === "admin" && (
              <details className="rounded-lg border p-4">
                <summary className="cursor-pointer font-medium">
                  {t("customer_deletions.title")}
                </summary>
                <Button
                  className="mt-4"
                  variant="destructive"
                  render={
                    <Link
                      href={newLocationCustomerDeletionPath(
                        locationId,
                        customer.id,
                      )}
                    />
                  }
                >
                  {t("customer_deletions.title")}
                </Button>
              </details>
            )}
          </aside>
        </div>
      </PageCanvas>
    </AppLayout>
  );
}
